import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
// Bundle the pdf.js worker locally (Vite ?url asset) — a CDN worker can be
// blocked by CSP/CORS and silently fail to render the preview.
// @ts-ignore — Vite resolves the ?url suffix to the built asset URL at build time.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { Sidebar } from '../../components/Sidebar';
import { Header } from '../../components/header';
import '../../scss/create-contract.scss';
import { fetchCustomers, Customer } from '../../api/customer';
import {
    createCustomerContractApi,
    getCustomerContractByIdApi,
    getAllCustomerContractsApi,
    getPreferredContractLanguageApi,
    previewContractPdfApi,
    sendContractAgreementEmail,
    CONTRACT_STATUS_ACTIVE,
} from '../../features/customer-contract/customer-contract.service';
import { fetchPersonalData } from '../../features/profile-settings/profile-settings.slice';
import { formatContractDate } from '../../features/customer-contract/customer-contract-date.util';
import { resolveContractLang, CONTRACT_LANG_STORAGE_KEY } from '../../features/customer-contract/customer-contract-language.util';
import {
    CheckIcon, StarsIcon,
    ImageIcon, PeopleIcon, WalletIcon, ScaleIcon,
} from './create-contract/icons';
import {
    Step0Props, Step1Props, Step2Props, Step3Props, StepBaseProps,
    ASSIGNMENT_PERIOD_MAX_LENGTH, OFFERED_SERVICES_MAX_LENGTH,
} from './create-contract/types';
import Step0LogoProfile             from './create-contract/step0-logo-profile.component';
import Step1Parties                 from './create-contract/step1-parties.component';
import Step2AssignmentCompensation  from './create-contract/step2-assignment-compensation.component';
import Step3Legal                   from './create-contract/step4-legal.component';

// pdf.js worker — served from our own bundle, version-matched to react-pdf's pdfjs.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Render the title with the LAST word styled as a violet gradient brand
// accent — works for any locale. Single-word titles are fully styled.
const renderHeroTitle = (title: string): React.ReactNode => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) return <span className="cc-hero__brand">{trimmed}</span>;
    return (
        <>
            {trimmed.slice(0, idx)}{' '}
            <span className="cc-hero__brand">{trimmed.slice(idx + 1)}</span>
        </>
    );
};

// First-mount shimmer skeleton that mirrors the stepper + first card layout.
const CcSkeleton: React.FC = () => (
    <>
        <div className="cc-skel cc-skel-stepper" />
        <div className="cc-skel-card">
            <div className="cc-skel-line cc-skel-line--title" />
            <div className="cc-skel-line" />
            <div className="cc-skel-line" />
            <div className="cc-skel-line cc-skel-line--short" />
        </div>
    </>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const CreateContractPage: React.FC = () => {
    const navigate     = useNavigate();
    const [lang, setLang] = useState(resolveContractLang);
    const language = new URLSearchParams(window.location.search).get('lang')
    const contractId   = new URLSearchParams(window.location.search).get('id');
    const duplicateId  = new URLSearchParams(window.location.search).get('duplicateId');
    const sourceId     = contractId || duplicateId;
    const isEditMode   = !!contractId;
    const isDuplicateMode = !contractId && !!duplicateId;
    const { t }        = useTranslation();
    const dispatch     = useDispatch<any>();
    const userId       = useSelector((state: any) => state.auth?.user?.userId);
    const personalData = useSelector((state: any) => state.profileSettings?.personalData);

    const steps = [
        { label: t('CC_STEP_COMPANY_LOGO'),             subtitle: t('CC_STEP_COMPANY_LOGO_SUB'),             icon: <ImageIcon /> },
        { label: t('CC_STEP_PARTIES_INFO'),             subtitle: t('CC_STEP_PARTIES_INFO_SUB'),             icon: <PeopleIcon /> },
        { label: t('CC_STEP_ASSIGNMENT_COMPENSATION'),  subtitle: t('CC_STEP_ASSIGNMENT_COMPENSATION_SUB'),  icon: <WalletIcon /> },
        { label: t('CC_STEP_LEGAL_SIGNATURES'),         subtitle: t('CC_STEP_LEGAL_SIGNATURES_SUB'),         icon: <ScaleIcon /> },
    ];

    // ── Shared UI state ───────────────────────────────────────────────────────

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentStep,   setCurrentStep]   = useState(0);
    const [isSubmitting,  setIsSubmitting]   = useState(false);
    const [submitAction,  setSubmitAction]   = useState<'draft' | 'send' | null>(null);
    const [errors,        setErrors]         = useState<Record<string, string>>({});
    const [isLoadingContract, setIsLoadingContract] = useState(!!sourceId);
    const [booting,           setBooting]           = useState(true);

    // Nothing is saved yet at preview time — the PDF is rendered from the
    // current in-memory form state via the preview-pdf endpoint. create +
    // send-email only happen once the sender confirms inside the modal.
    interface ContractPreview {
        pdfUrl: string;
        senderName: string;
    }
    const [previewContract, setPreviewContract] = useState<ContractPreview | null>(null);
    const [isSendingFromPreview, setIsSendingFromPreview] = useState(false);

    // ── PDF preview rendering (react-pdf) + custom swipe navigation ────────────
    const [previewNumPages, setPreviewNumPages] = useState(0);
    const [previewPageWidth, setPreviewPageWidth] = useState(0);
    const [previewAtTop,     setPreviewAtTop]     = useState(true);
    const [previewAtBottom,  setPreviewAtBottom]  = useState(false);
    const previewScrollRef = useRef<HTMLDivElement>(null);

    const syncPreviewEdges = () => {
        const el = previewScrollRef.current;
        if (!el) return;
        setPreviewAtTop(el.scrollTop <= 2);
        setPreviewAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
    };

    // Smooth "swipe" scroll — dir 1 = down, -1 = up (~85% of a viewport).
    const swipePreview = (dir: 1 | -1) => {
        const el = previewScrollRef.current;
        if (!el) return;
        el.scrollBy({ top: dir * el.clientHeight * 0.85, behavior: 'smooth' });
    };

    // Fit each page to the frame width; reset scroll edges when the modal opens.
    useEffect(() => {
        if (!previewContract) return;
        const measure = () => {
            const el = previewScrollRef.current;   // the scrollable modal body
            if (el) setPreviewPageWidth(Math.max(0, el.clientWidth - 44));
        };
        measure();
        setPreviewNumPages(0);
        setPreviewAtTop(true);
        setPreviewAtBottom(false);
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [previewContract]);

    // Re-check edges once pages have laid out (content height is now known).
    useEffect(() => { syncPreviewEdges(); }, [previewNumPages, previewPageWidth]);

    // ── Step 0 ────────────────────────────────────────────────────────────────
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    // ── Step 1 ────────────────────────────────────────────────────────────────
    const [customers,            setCustomers]            = useState<Customer[]>([]);
    const [selectedCustomerId,   setSelectedCustomerId]   = useState<number | null>(null);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [offeredServices,      setOfferedServices]      = useState('');
    const [assignmentPeriod,     setAssignmentPeriod]     = useState('');
    const [workDescription,      setWorkDescription]      = useState('');
    const [specialTerms,         setSpecialTerms]         = useState('');

    // ── Step 2 (Assignment + Compensation) ────────────────────────────────────
    const [startDate,       setStartDate]       = useState('');
    const [endDate,         setEndDate]         = useState('');
    const [noEndDate,       setNoEndDate]       = useState(false);
    const [hasNoticePeriod, setHasNoticePeriod] = useState(false);
    const [noticePeriod,    setNoticePeriod]    = useState('');
    const [assignmentDesc,  setAssignmentDesc]  = useState('');
    const [compType,        setCompType]        = useState('');
    const [unit,            setUnit]            = useState('');
    const [currency,        setCurrency]        = useState('SEK');
    const [amount,          setAmount]          = useState('');

    // ── Step 3 (Legal + Signatures) ───────────────────────────────────────────
    const [hasAgreed,    setHasAgreed]    = useState(false);
    // Always today's date — not user-editable, so it isn't loaded from a
    // saved contract or exposed as an input the user can change.
    const [signDate]                      = useState(() => formatContractDate(new Date().toISOString()));
    // personalData comes off an untyped selector, so pin the state to string —
    // otherwise the updater callbacks below infer an implicit `any`.
    const [signerName,   setSignerName]   = useState<string>(personalData?.fullName || '');
    const [hasSignature, setHasSignature] = useState(false);
    const [existingSignatureData, setExistingSignatureData] = useState<string | undefined>(undefined);
    // Status of the contract being edited (null for a fresh create/duplicate)
    // — an already-Active contract can't be sent for signature again.
    const [loadedContractStatus, setLoadedContractStatus] = useState<number | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // ── First-mount skeleton (~1s) ──────────────────────────────────────────────
    useEffect(() => {
        const id = setTimeout(() => setBooting(false), 1000);
        return () => clearTimeout(id);
    }, []);

    // Scoped to this contract only — does not touch the platform-wide UI
    // language (that's the Header's own dropdown).
    const handleContractLangChange = (newLang: string) => {
        setLang(newLang);
        window.localStorage.setItem(CONTRACT_LANG_STORAGE_KEY, newLang);
    };
    useEffect(() => {
        if (window.localStorage.getItem(CONTRACT_LANG_STORAGE_KEY)) return;

        getPreferredContractLanguageApi(lang)
            .then(res => {
                const preferred = res.response;
                if (preferred && preferred !== lang) handleContractLangChange(preferred);
            })
            .catch(() => { /* keep the locally-resolved default */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefill the signer's name clarification from the profile once it loads
    // (fresh create only; never overwrites a loaded contract or user edits).
    useEffect(() => {
        if (contractId) return; // edit mode loads the saved name instead
        const profileName =
            personalData?.fullName?.trim() ||
            [personalData?.firstName, personalData?.lastName].filter(Boolean).join(' ').trim();
        if (profileName) setSignerName(prev => (prev.trim() ? prev : profileName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalData, contractId]);

    // ── Data loading ──────────────────────────────────────────────────────────

    useEffect(() => {
        const loadLogo = async () => {
            if (!userId) return;
            try {
                const userData = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
                const token    = userData.token || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
                const baseUrl  = (import.meta as any).env.VITE_API_URL || '';
                const res      = await fetch(`${baseUrl}/signup/invoice-logo-stream?userId=${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const blob = await res.blob();
                    if (blob.size > 0) setLogoUrl(URL.createObjectURL(blob));
                }
            } catch { /* no logo */ }
        };

        const loadCustomers = async () => {
            try {
                const res = await fetchCustomers({ pageSize: 100 });
                setCustomers(res.response || []);
            } catch {
                toast.error(t('CC_TOAST_ERROR') as string);
            }
        };

        loadLogo();
        loadCustomers();
        dispatch(fetchPersonalData(userId));
    }, [userId]);

    useEffect(() => {
        console.log(personalData?.firstName,'personalData?.fullName')
        if (personalData?.firstName) setSignerName(personalData.firstName);
    }, [personalData?.firstName]);

    // ── Load existing contract for edit ─────────────────────────────────────────

    useEffect(() => {
        if (!sourceId) return;

        const loadContract = async () => {
            try {
                const res = await getCustomerContractByIdApi(Number(sourceId));
                const c = res.response;

                setSelectedCustomerId(c.customer?.customerId ?? null);
                setOfferedServices(c.offeredServices || '');
                setAssignmentPeriod(c.assignmentPeriod || '');
                setWorkDescription(c.workDescription || '');
                setSpecialTerms(c.specialTerms || '');

                setHasNoticePeriod(c.hasNoticePeriod);
                setNoticePeriod(c.noticePeriodDuration || '');
                setAssignmentDesc(c.assignmentDescription || '');

                setCompType(c.compensationType || '');
                setUnit(c.numberOfUnits != null ? String(c.numberOfUnits) : '');
                setCurrency(c.currency || 'SEK');
                setAmount(c.amount != null ? String(c.amount) : '');

                if (isDuplicateMode) {
                    setStartDate('');
                    setEndDate('');
                    setNoEndDate(false);
                } else {
                    setStartDate(c.startDate ? c.startDate.split('T')[0] : '');
                    setEndDate(c.endDate ? c.endDate.split('T')[0] : '');
                    setNoEndDate(c.noEndDate);
                    setLoadedContractStatus(c.contractStatus);

                    setHasAgreed(c.isTermsAccepted);
                    if (c.freelancerNameClarification) setSignerName(c.freelancerNameClarification);
                    if (c.freelancerSignatureData) setExistingSignatureData(c.freelancerSignatureData);
                }
            } catch {
                toast.error(t('CC_TOAST_LOAD_ERROR') as string);
                navigate(`/v2/my-contracts?lang=${language}`);
            } finally {
                setIsLoadingContract(false);
            }
        };

        loadContract();
    }, [sourceId]);

    // ── Validation ────────────────────────────────────────────────────────────

    const validateStep = (step: number): Record<string, string> => {
        const e: Record<string, string> = {};

        if (step === 1) {
            if (!selectedCustomerId)     e.customer        = t('CC_VAL_CUSTOMER_REQUIRED');
            if (!offeredServices.trim()) {
                e.offeredServices = t('CC_VAL_FIELD_REQUIRED');
            } else if (offeredServices.length > OFFERED_SERVICES_MAX_LENGTH) {
                e.offeredServices = t('CC_VAL_OFFERED_SERVICES_MAX');
            }
            if (!workDescription.trim()) e.workDescription = t('CC_VAL_FIELD_REQUIRED');
            if (assignmentPeriod.length > ASSIGNMENT_PERIOD_MAX_LENGTH) {
                e.assignmentPeriod = t('CC_VAL_ASSIGN_PERIOD_MAX');
            }
        }

        if (step === 2) {
            if (!startDate) e.startDate = t('CC_VAL_FIELD_REQUIRED');
            if (!noEndDate && !endDate) {
                e.endDate = t('CC_VAL_END_DATE_REQUIRED');
            } else if (startDate && endDate && !noEndDate && new Date(endDate) <= new Date(startDate)) {
                e.endDate = t('CC_VAL_END_AFTER_START');
            }
            if (!assignmentDesc.trim())                           e.assignmentDesc = t('CC_VAL_FIELD_REQUIRED');
            if (!compType)                                        e.compType       = t('CC_VAL_FIELD_REQUIRED');
            if (compType === 'per_unit' && !unit.trim())         e.unit           = t('CC_VAL_FIELD_REQUIRED');
            if (!amount || parseFloat(amount) <= 0)              e.amount         = t('CC_VAL_AMOUNT_POSITIVE');
        }

        if (step === 3) {
            if (!hasSignature)      e.signature  = t('CC_VAL_SIGNATURE_REQUIRED');
            if (!signerName.trim()) e.signerName = t('CC_VAL_FIELD_REQUIRED');
            if (!hasAgreed)         e.hasAgreed  = t('CC_VAL_TERMS_REQUIRED');
        }

        return e;
    };

    // Maps each validation error key → an existing (already-localised) UI label
    // key, so the error toast can name the unfilled field(s) in any language.
    const FIELD_LABEL_KEYS: Record<string, string> = {
        customer:        'CCG_COL_CUSTOMER',
        offeredServices: 'CC_PARTIES_OFFERED_SERVICES_LABEL',
        workDescription: 'CC_PARTIES_WORK_DESC_LABEL',
        assignmentPeriod:'CC_PARTIES_ASSIGN_PERIOD_LABEL',
        startDate:       'CC_ASSIGN_START_DATE',
        endDate:         'CC_ASSIGN_END_DATE',
        assignmentDesc:  'CC_ASSIGN_DESC_TITLE',
        compType:        'CC_COMP_TYPE_LABEL',
        unit:            'CC_COMP_UNIT_LABEL',
        amount:          'CC_COMP_AMOUNT_LABEL',
        signature:       'CC_SIG_SIGNATURE_LABEL',
        signerName:      'CC_SIG_NAME_LABEL',
        hasAgreed:       'CC_LEGAL_CARD_TITLE',
    };

    // Toast the validation problems (localised). Errors that only say "this field
    // is required" are grouped into one line naming the empty fields; every other
    // error already explains itself (length limits, date order, amount, …) and is
    // shown verbatim so the toast matches the inline field error.
    const showValidationToast = (e: Record<string, string>) => {
        const requiredMsg = String(t('CC_VAL_FIELD_REQUIRED'));
        const keys = Object.keys(e);
        const emptyKeys = keys.filter(k => e[k] === requiredMsg);
        const detailedMsgs = keys.filter(k => e[k] !== requiredMsg).map(k => e[k]);

        const emptyLabels = emptyKeys
            .map(k => FIELD_LABEL_KEYS[k])
            .filter(Boolean)
            .map(key => String(t(key)).replace(/[:：]\s*$/, '').trim());

        const parts: string[] = [];
        if (emptyLabels.length) {
            parts.push(`${t('CC_TOAST_FILL_REQUIRED')}: ${emptyLabels.join(', ')}`);
        } else if (emptyKeys.length) {
            parts.push(t('CC_TOAST_FILL_REQUIRED') as string);
        }
        parts.push(...detailedMsgs);

        toast.error(parts.length ? parts.join(' ') : (t('CC_TOAST_FILL_REQUIRED') as string));
    };

    // After a failed validation, bring the first invalid field into view so the
    // user sees exactly what's missing (runs after the error classes render).
    const scrollToFirstError = () => {
        requestAnimationFrame(() => {
            const el = document.querySelector(
                '.cc-step-content .cc-input--error, .cc-step-content .cc-canvas-wrap--error, .cc-step-content .cc-confirm-row--error, .cc-step-content .cc-field-error'
            ) as HTMLElement | null;
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (el.matches('input, textarea, select')) {
                (el as HTMLInputElement).focus({ preventScroll: true });
            }
        });
    };

    // ── Navigation ────────────────────────────────────────────────────────────

    const goNext = () => {
        const e = validateStep(currentStep);
        if (Object.keys(e).length > 0) { setErrors(e); showValidationToast(e); scrollToFirstError(); return; }
        setErrors({});
        setCurrentStep(s => Math.min(s + 1, steps.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goBack = () => {
        setErrors({});
        setCurrentStep(s => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // The create/update endpoint doesn't return the saved contract's id, so
    // when sending a brand-new (or duplicated) contract we look it up by
    // customer right after saving, picking the most recently created match.
    const findJustSavedContractId = async (customerId: number): Promise<number | null> => {
        try {
            const res = await getAllCustomerContractsApi();
            const matches = (res.response || []).filter(c => c.customer?.customerId === customerId);
            if (matches.length === 0) return null;
            matches.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
            return matches[0].id;
        } catch {
            return null;
        }
    };

    // Shared field set for both the create payload and the preview-pdf
    // payload — always reads whatever is currently on the canvas/form.
    const buildCreatePayload = (id?: number) => {
        const signatureData = (canvasRef.current?.toDataURL('image/png') || '').split(',')[1] || '';
        return {
            ...(id ? { id } : {}),
            customerId:                selectedCustomerId!,
            offeredServices,
            assignmentPeriod,
            workDescription,
            specialTerms,
            startDate:                 startDate ? new Date(startDate).toISOString() : '',
            endDate:                   endDate   ? new Date(endDate).toISOString()   : '',
            noEndDate,
            hasNoticePeriod,
            noticePeriodDuration:      noticePeriod,
            assignmentDescription:     assignmentDesc,
            compensationType:          compType,
            numberOfUnits:             compType === 'per_unit' ? (parseFloat(unit) || 0) : 0,
            currency,
            amount:                    parseFloat(amount) || 0,
            isTermsAccepted:           hasAgreed,
            freelancerSignatureDate:     signDate ? new Date(signDate).toISOString() : '',
            freelancerSignatureData:     signatureData || existingSignatureData || '',
            freelancerNameClarification: signerName,
            language:                      lang,
        };
    };

    const handleSave = async (action: 'draft' | 'send') => {
        if (action === 'send' && loadedContractStatus === CONTRACT_STATUS_ACTIVE) {
            toast.error(t('CCG_ACTION_SEND_DISABLED_ACTIVE') as string);
            return;
        }

        const e = validateStep(3);
        if (Object.keys(e).length > 0) { setErrors(e); showValidationToast(e); scrollToFirstError(); return; }
        if (!selectedCustomerId) { toast.error(t('CC_TOAST_CUSTOMER_REQUIRED') as string); return; }

        try {
            setIsSubmitting(true);
            setSubmitAction(action);

            if (action === 'draft') {
                await createCustomerContractApi(buildCreatePayload(isEditMode ? Number(contractId) : undefined));
                toast.success(t(isEditMode ? 'CC_TOAST_UPDATE_SUCCESS' : 'CC_TOAST_SUCCESS') as string);
                navigate(`/v2/my-contracts?lang=${language}`);
                return;
            }

            // action === 'send' — preview only. Nothing is created/saved and
            // no email is sent until the sender confirms inside the modal.
            const customer = customers.find(c => c.customerId === selectedCustomerId);
            if (!customer?.email) {
                toast.error(t('CCG_SEND_NO_EMAIL') as string);
                return;
            }

            const signatureData = (canvasRef.current?.toDataURL('image/png') || '').split(',')[1] || '';
            const pdfBlob = await previewContractPdfApi({
                ...(isEditMode ? { id: Number(contractId) } : {}),
                customerId:                selectedCustomerId,
                language:                  lang,
                offeredServices,
                assignmentPeriod,
                workDescription,
                specialTerms,
                startDate:                 startDate ? new Date(startDate).toISOString() : '',
                endDate:                   endDate   ? new Date(endDate).toISOString()   : '',
                noEndDate,
                hasNoticePeriod,
                noticePeriodDuration:      noticePeriod,
                assignmentDescription:     assignmentDesc,
                compensationType:          compType,
                currency,
                amount:                    parseFloat(amount) || 0,
                numberOfUnits:             compType === 'per_unit' ? (parseFloat(unit) || 0) : 0,
                isTermsAccepted:           hasAgreed,
                customerSignatureDate:     '',
                customerSignatureData:     '',
                customerNameClarification: '',
                freelancerSignatureDate:     signDate ? new Date(signDate).toISOString() : '',
                freelancerSignatureData:     signatureData || existingSignatureData || '',
                freelancerNameClarification: signerName,
                isDraft: true,
            }, lang);

            const senderName =
                personalData?.fullName?.trim() ||
                [personalData?.firstName, personalData?.lastName].filter(Boolean).join(' ').trim();

            setPreviewContract({ pdfUrl: URL.createObjectURL(pdfBlob), senderName });
        } catch {
            toast.error(t(
                action === 'send'
                    ? 'CC_TOAST_SEND_ERROR'
                    : (isEditMode ? 'CC_TOAST_UPDATE_ERROR' : 'CC_TOAST_ERROR')
            ) as string);
        } finally {
            setIsSubmitting(false);
            setSubmitAction(null);
        }
    };

    const closePreview = () => {
        if (previewContract) URL.revokeObjectURL(previewContract.pdfUrl);
        setPreviewContract(null);
    };

    const handleConfirmSendFromPreview = async () => {
        if (!previewContract || !selectedCustomerId) return;
        try {
            setIsSendingFromPreview(true);

            // "Send to Customer" is the point of no return — save the
            // contract for real, then email it.
            await createCustomerContractApi(buildCreatePayload(isEditMode ? Number(contractId) : undefined));

            const targetId = isEditMode ? Number(contractId) : await findJustSavedContractId(selectedCustomerId);
            if (!targetId) {
                toast.error(t('CC_TOAST_SEND_LOOKUP_ERROR') as string);
                closePreview();
                navigate(`/v2/my-contracts?lang=${language}`);
                return;
            }

            const detail = (await getCustomerContractByIdApi(targetId)).response;
            if (!detail.customer?.email) {
                toast.error(t('CCG_SEND_NO_EMAIL') as string);
                closePreview();
                navigate(`/v2/my-contracts?lang=${language}`);
                return;
            }

            await sendContractAgreementEmail(
                targetId,
                {
                    customerName:     detail.customer.name,
                    customerEmail:    detail.customer.email,
                    contractNumber:   detail.contractNumber,
                    startDate:        detail.startDate,
                    endDate:          detail.endDate,
                    noEndDate:        detail.noEndDate,
                    amount:           detail.amount,
                    currency:         detail.currency,
                    compensationType: detail.compensationType,
                },
                previewContract.senderName,
                lang,
                detail.contractStatus
            );

            toast.success(t('CC_TOAST_SEND_SUCCESS') as string);
            closePreview();
            navigate(`/v2/my-contracts?lang=${language}`);
        } catch {
            toast.error(t('CC_TOAST_SEND_ERROR') as string);
        } finally {
            setIsSendingFromPreview(false);
        }
    };

    // ── renderStep ────────────────────────────────────────────────────────────

    const renderStep = (): React.ReactNode => {
        const shared: StepBaseProps = { t, errors, setErrors };

        switch (currentStep) {
            case 0: {
                const props: Step0Props = {
                    ...shared,
                    logoUrl,
                    personalData,
                    lang,
                    onLangChange: handleContractLangChange,
                    onNavigateToProfile: () => navigate(`/profile-settings?tab=preferences&lang=${lang}`),
                };
                return <Step0LogoProfile {...props} />;
            }
            case 1: {
                const props: Step1Props = {
                    ...shared,
                    personalData,
                    customers,
                    selectedCustomerId,
                    setSelectedCustomerId,
                    showCustomerDropdown,
                    setShowCustomerDropdown,
                    offeredServices,
                    setOfferedServices,
                    assignmentPeriod,
                    setAssignmentPeriod,
                    workDescription,
                    setWorkDescription,
                    specialTerms,
                    setSpecialTerms,
                    onCustomerAdded: async () => {
                        try {
                            const res = await fetchCustomers({ pageSize: 100 });
                            setCustomers(res.response || []);
                        } catch { /* keep existing list */ }
                    },
                };
                return <Step1Parties {...props} />;
            }
            case 2: {
                const props: Step2Props = {
                    ...shared,
                    startDate,   setStartDate,
                    endDate,     setEndDate,
                    noEndDate,   setNoEndDate,
                    hasNoticePeriod, setHasNoticePeriod,
                    noticePeriod,    setNoticePeriod,
                    assignmentDesc,  setAssignmentDesc,
                    compType,    setCompType,
                    unit,        setUnit,
                    currency,    setCurrency,
                    amount,      setAmount,
                };
                return <Step2AssignmentCompensation {...props} />;
            }
            case 3: {
                const props: Step3Props = {
                    ...shared,
                    hasAgreed,    setHasAgreed,
                    signDate,
                    signerName,   setSignerName,
                    hasSignature, setHasSignature,
                    canvasRef,
                    initialSignatureData: existingSignatureData,
                    customerName: customers.find(c => c.customerId === selectedCustomerId)?.name,
                    isCustomerSigned: loadedContractStatus === CONTRACT_STATUS_ACTIVE,
                };
                return <Step3Legal {...props} />;
            }
            default:
                return null;
        }
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId="contracts"
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
                )}

                <div className="dashboard-main-content cc-contract-page">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />

                    <div className="cc-page-wrapper">

                        {/* Hero header banner */}
                        <div className="cc-hero">
                            <span className="cc-hero__badge"><StarsIcon /> {t('CC_HEADER_EYEBROW')}</span>
                            <h1 className="cc-hero__title">
                                {renderHeroTitle(t(isEditMode ? 'CC_PAGE_TITLE_EDIT' : 'CC_PAGE_TITLE'))}
                            </h1>
                            <p className="cc-hero__subtitle">{t('CC_PAGE_SUBTITLE')}</p>
                        </div>

                        {(booting || isLoadingContract) ? (
                            <CcSkeleton />
                        ) : (
                        <>

                        {/* Stepper */}
                        <div className="cc-stepper">
                            {steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div
                                        className={`cc-step-item${idx === currentStep ? ' active' : ''}${idx < currentStep ? ' completed' : ''}`}
                                        onClick={() => idx < currentStep ? setCurrentStep(idx) : undefined}
                                        style={{ cursor: idx < currentStep ? 'pointer' : 'default' }}
                                    >
                                        <div className="cc-step-circle">
                                            {idx < currentStep ? <CheckIcon /> : step.icon}
                                        </div>
                                        <span className="cc-step-textwrap">
                                            <span className="cc-step-label">{step.label}</span>
                                            <span className="cc-step-subtitle">{step.subtitle}</span>
                                        </span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className={`cc-step-connector${idx < currentStep ? ' done' : ''}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Active step */}
                        <div key={currentStep} className="cc-step-content">
                            {renderStep()}
                        </div>

                        {/* Footer */}
                        <div className="cc-footer-bar">
                            <button className="cc-btn-cancel" type="button" onClick={() => navigate(-1)}>
                                {t('CC_BTN_CANCEL')}
                            </button>
                            <div className="cc-footer-right">
                                {currentStep > 0 && (
                                    <button className="cc-btn-back" type="button" onClick={goBack}>
                                        {t('CC_BTN_BACK')}
                                    </button>
                                )}
                                {currentStep < steps.length - 1 ? (
                                    <button className="cc-btn-next" type="button" onClick={goNext}>
                                        {t('CC_BTN_NEXT')}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="cc-btn-draft"
                                            type="button"
                                            onClick={() => handleSave('draft')}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting && submitAction === 'draft'
                                                ? <><span className="cc-spinner" /> {t('CC_BTN_SAVING_DRAFT')}</>
                                                : <>{t('CC_BTN_SAVE_DRAFT')}</>
                                            }
                                        </button>
                                        <button
                                            className="cc-btn-submit"
                                            type="button"
                                            onClick={() => handleSave('send')}
                                            disabled={isSubmitting || loadedContractStatus === CONTRACT_STATUS_ACTIVE}
                                            title={loadedContractStatus === CONTRACT_STATUS_ACTIVE ? (t('CCG_ACTION_SEND_DISABLED_ACTIVE') as string) : undefined}
                                        >
                                            {isSubmitting && submitAction === 'send'
                                                ? <><span className="cc-spinner" /> {t('CC_BTN_SENDING')}</>
                                                : <>{t('CC_BTN_SEND_AGREEMENT')} <CheckIcon /></>
                                            }
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        </>
                        )}
                    </div>
                </div>
            </div>

            {previewContract && createPortal(
                <div className="cc-preview-modal-overlay" role="presentation" onClick={closePreview}>
                    <div
                        className="cc-preview-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cc-preview-modal-title"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="cc-preview-modal__header">
                            <span className="cc-preview-modal__header-icon" aria-hidden="true">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 3L10.5 13.5M21 3l-6.5 18-4-8-8-4L21 3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <div className="cc-preview-modal__header-text">
                                <h3 id="cc-preview-modal-title">{t('CC_PREVIEW_MODAL_TITLE')}</h3>
                                <p className="cc-preview-modal__subtitle">{t('CC_PREVIEW_MODAL_SUBTITLE')}</p>
                            </div>
                            <div className="cc-preview-modal__header-actions">
                                <a
                                    className="cc-preview-modal__download"
                                    href={previewContract.pdfUrl}
                                    download="customer-agreement.pdf"
                                    aria-label={t('CC_PREVIEW_DOWNLOAD') as string}
                                    data-tip={t('CC_PREVIEW_DOWNLOAD') as string}
                                >
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M12 3v11m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <span>{t('CC_PREVIEW_DOWNLOAD')}</span>
                                </a>
                                <button
                                    className="cc-preview-modal__close"
                                    type="button"
                                    onClick={closePreview}
                                    disabled={isSendingFromPreview}
                                    aria-label={t('CC_BTN_CANCEL') as string}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div
                            className="cc-preview-modal__body"
                            ref={previewScrollRef}
                            onScroll={syncPreviewEdges}
                        >
                            <div className="cc-preview-modal__pdf">
                                <Document
                                    file={previewContract.pdfUrl}
                                    onLoadSuccess={({ numPages }: { numPages: number }) => setPreviewNumPages(numPages)}
                                    loading={<div className="cc-preview-modal__pdf-msg"><span className="cc-spinner cc-spinner--brand" /></div>}
                                    error={<div className="cc-preview-modal__pdf-msg">{t('CC_TOAST_LOAD_ERROR')}</div>}
                                >
                                    {Array.from({ length: previewNumPages }).map((_, i) => (
                                        <Page
                                            key={i}
                                            pageNumber={i + 1}
                                            width={previewPageWidth || undefined}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            className="cc-preview-modal__pdf-page"
                                        />
                                    ))}
                                </Document>
                            </div>

                            <div className="cc-preview-modal__recipient">
                                <span className="cc-preview-modal__recipient-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                                        <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <p className="cc-preview-modal__recipient-text">
                                    {t('CC_PREVIEW_SEND_TO', {
                                        name: customers.find(c => c.customerId === selectedCustomerId)?.name ?? '',
                                        email: customers.find(c => c.customerId === selectedCustomerId)?.email ?? '',
                                    })}
                                </p>
                            </div>
                        </div>

                        {/* Floating pager — scrolls the modal content up / down */}
                        <div className="cc-preview-modal__pager" aria-hidden={previewNumPages === 0}>
                            <button
                                type="button"
                                className="cc-preview-modal__pager-btn cc-preview-modal__pager-btn--up"
                                onClick={() => swipePreview(-1)}
                                disabled={previewAtTop}
                                data-tip={t('CC_PREVIEW_SWIPE_UP') as string}
                                aria-label={t('CC_PREVIEW_SWIPE_UP') as string}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <button
                                type="button"
                                className="cc-preview-modal__pager-btn cc-preview-modal__pager-btn--down"
                                onClick={() => swipePreview(1)}
                                disabled={previewAtBottom}
                                data-tip={t('CC_PREVIEW_SWIPE_DOWN') as string}
                                aria-label={t('CC_PREVIEW_SWIPE_DOWN') as string}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>

                        <div className="cc-preview-modal__footer">
                            <button
                                className="cc-btn-back"
                                type="button"
                                onClick={closePreview}
                                disabled={isSendingFromPreview}
                            >
                                {t('CC_BTN_CANCEL')}
                            </button>
                            <button
                                className="cc-btn-submit"
                                type="button"
                                onClick={handleConfirmSendFromPreview}
                                disabled={isSendingFromPreview}
                            >
                                {isSendingFromPreview
                                    ? <><span className="cc-spinner" /> {t('CC_BTN_SENDING')}</>
                                    : <>{t('CC_BTN_CONFIRM_SEND')} <CheckIcon /></>
                                }
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CreateContractPage;
